import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { concatMap, map, take } from 'rxjs/operators';
import { StudentSearchComponent } from '../../student-search/student-search.component';
import { Student } from '../../student/student.model';
import { StudentService } from '../../student/student.service';
import { School } from '../../year/school.model';
import { LoginService } from './../../../shared-modules/login/login.service';
import { UserService } from './../../user/user.service';
import { YearService } from './../../year/year.service';

/**
 * This makes a selected student required for the route
 * It takes studentSearchInjectedData as the data that will be injected into the dialog
 */
@Injectable()
export class RequireStudentGuard implements CanActivate, CanActivateChild {

    // Properties

    // Class Functions
    constructor(
        private dialog: MatDialog,
        private studentService: StudentService,
        private loginService: LoginService,
        private userService: UserService,
        private yearService: YearService
    ) { }

    // Methods
    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
        return this.canActivate(childRoute, state);
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
        // POST: opens the student selector and, when a student is selected, allows you to continue
        if (
            !this.studentService.selectedStudent
            && (
                !route.data.optionalStudentPortal
                || route.queryParams['studentPortal']
            )
        ) {

            // If we pass in school permissions, we have to look those up to determine which schools should be enabled
            let schoolPermissions: number[] = [];
            if (this.loginService.LoginSettings.V10) {
                schoolPermissions = route.data.studentSearchSchoolPermissionsV10;
            } else {
                schoolPermissions = route.data.studentSearchSchoolPermissionsV9;
            }
            if (schoolPermissions && schoolPermissions.length > 0) {
                const studentSearchSchools: School[] = [];
                return this.userService.getUserPermissionByPrivilegeIds(
                    this.yearService.defaultYear.Id,
                    0,
                    schoolPermissions
                ).pipe(
                    concatMap(permissions => {
                        permissions.forEach(permission => {
                            if (permission.SiteId) {
                                const school = this.yearService.allDistrictSchools.find(s => s.Id == permission.SiteId);
                                if (school && !studentSearchSchools.includes(school)) {
                                    studentSearchSchools.push(school);
                                }
                            }
                        });
                        let injectedCriteria: any = {
                            schools: studentSearchSchools
                        };
                        if (route.data.studentSearchInjectedData) {
                            injectedCriteria = route.data.studentSearchInjectedData;
                            injectedCriteria['schools'] = studentSearchSchools;
                        }
                        return this.performStudentSearch(injectedCriteria);
                    }
                    ));
            }

            // If no school permissions are passed, just open the student search
            else {
                return this.performStudentSearch(route.data.studentSearchInjectedData);
            }
        } else {
            return true;
        }
    }

    performStudentSearch(injectedCriteria: any) {
        const searchCriteria = {
            performRoute: false,
            searchAndSelect: false,
            returnSingle: false,
            returnAll: true,
            useLastSelectedSchool: false
        };
        if (injectedCriteria) {
            Object.keys(injectedCriteria).forEach(key => {
                searchCriteria[key] = injectedCriteria[key];
            });
        }
        const dialogBox = this.dialog.open(StudentSearchComponent, {
            disableClose: true,
            width: '100%',
            height: '100%',
            maxHeight: '100vh',
            maxWidth: '100vw',
            data: searchCriteria
        });
        return dialogBox.afterClosed().pipe(
            take(1),
            map((student: Student) => {
                if (student) {
                    this.studentService.selectedStudent = student;
                    return true;
                } else {
                    return false;
                }
            })
        );
    }
}
